import classNames from 'classnames'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { match } from 'ts-pattern'
import { DefaultButton } from '../../components/buttons/default-button.tsx'
import { ErrorBoundaryFallback } from '../../components/error-boundaries/error-boundary-fallback.tsx'
import { CreateProjectModal } from '../../components/modals/create-project-modal.tsx'
import * as layout from '../../components/projects/project-page-layout.css.ts'
import { RepositoryNames } from '../../components/projects/repository-names.tsx'
import { useReturnFocus } from '../../components/projects/return-focus.ts'
import { ScopeHierarchyHint } from '../../components/projects/scope-hierarchy-hint.tsx'
import { useCloseOnLostPermission } from '../../components/projects/use-close-on-lost-permission.ts'
import { isInteractiveElementClick } from '../../components/table/row-interaction.ts'
import { TableBodySkeleton } from '../../components/table/table-body-skeleton.tsx'
import { Routes } from '../../routes.ts'
import { useGetProjects, useGetRepositoryCount } from '../../utils/api-client/api-hooks.ts'
import type { Project } from '../../utils/api-client/api-schemas.ts'
import { type AvailableTemplate, getTemplate } from '../../utils/api-client/templates.ts'
import { ErrorDetail } from '../../utils/error-handling/error-detail.tsx'
import * as styles from './projects-page.css.ts'

export const ProjectsPage: React.FC = () => {
  const navigate = useNavigate()
  // This page is the one that shows member names, so it is the one that embeds the repositories.
  const { projects, collection, isPending, error, refetch } = useGetProjects({ embedRepositories: true })
  const create = getTemplate(collection, 'create')
  const { data: repositoryCount } = useGetRepositoryCount()
  const [createTemplate, setCreateTemplate] = useState<AvailableTemplate>()
  // The dialog mounts already open, so Radix has no trigger to put focus back on. @see useReturnFocus
  const { captureOpener, onCloseAutoFocus, fallbackRef } = useReturnFocus<HTMLHeadingElement>()
  useCloseOnLostPermission({
    isOpen: createTemplate !== undefined,
    isAvailable: create.state !== 'unavailable' && create.state !== 'not-adopted',
    onClose: () => setCreateTemplate(undefined),
    message: <>That change is no longer available.</>,
  })

  const grouped = useMemo(() => projects.reduce((sum, project) => sum + project.repository_count, 0), [projects])

  return (
    <div className={layout.page}>
      <div className={layout.titleBlock}>
        {/* Where focus goes when the create dialog closes itself and takes its opener with it. */}
        <h1 className={layout.heading} ref={fallbackRef} tabIndex={-1}>
          Projects
        </h1>
        <p className={layout.description}>
          Group repositories to scope feedback
          <ScopeHierarchyHint />
        </p>
      </div>

      <div className={layout.bar}>
        {/* Only from settled answers: the grouped count is a sum over the projects, so it reads 0 while
            they load or after they failed — a claim that nothing is grouped. The paragraph itself stays,
            holding the create control at the end of the bar. */}
        <p className={layout.summary}>
          {!isPending && !error && repositoryCount !== undefined && (
            <>
              {grouped} of {repositoryCount} <span className={layout.summaryMuted}>repositories grouped</span>
            </>
          )}
        </p>
        {!error &&
          match(create)
            .with({ state: 'loading' }, () => (
              <DefaultButton size="small" state="loading">
                + Create project
              </DefaultButton>
            ))
            .with({ state: 'available' }, ({ method, target }) => (
              <DefaultButton
                size="small"
                onClick={() => {
                  captureOpener()
                  setCreateTemplate({ method, target })
                }}
              >
                + Create project
              </DefaultButton>
            ))
            .with({ state: 'unavailable' }, { state: 'not-adopted' }, () => (
              <p className={layout.readOnlyNotice}>You are not authorized to create projects in this organization.</p>
            ))
            .exhaustive()}
      </div>

      {/* A failed query is not an empty estate: "No projects yet" would tell the reader their projects
          are gone when the page simply could not read them. */}
      {error ? (
        <ErrorBoundaryFallback
          title="Unable to load projects"
          detail={<ErrorDetail error={error} />}
          onRetry={() => void refetch()}
        />
      ) : isPending || projects.length > 0 ? (
        <ProjectsTable projects={projects} isPending={isPending} />
      ) : (
        <div className={layout.emptyCard}>
          <h3 className={layout.emptyTitle}>No projects yet</h3>
          <p className={layout.emptyText}>
            Feedback and preferences are solely on a per-repository basis until projects are defined.
          </p>
        </div>
      )}

      {createTemplate && (
        <CreateProjectModal
          template={createTemplate}
          onCloseAutoFocus={onCloseAutoFocus}
          onClose={() => setCreateTemplate(undefined)}
          onCreated={project => {
            setCreateTemplate(undefined)
            void navigate(Routes.ProjectDetailsPage.createPath({ projectId: project.id }))
          }}
        />
      )}
    </div>
  )
}

const ProjectsTable: React.FC<{ projects: Project[]; isPending: boolean }> = ({ projects, isPending }) => {
  const navigate = useNavigate()

  const openProject = (projectId: string) => void navigate(Routes.ProjectDetailsPage.createPath({ projectId }))

  return (
    <div className={styles.scroll}>
      <table className={styles.table}>
        <colgroup>
          <col className={styles.projectColumn} />
          <col />
        </colgroup>
        <thead>
          <tr className={styles.headRow}>
            <th className={styles.columnHeader}>Project</th>
            <th className={styles.columnHeader}>Repositories</th>
          </tr>
        </thead>
        {isPending ? (
          <TableBodySkeleton columnCount={2} rowCount={3} rowHeightInPixels={72} label="Loading projects" />
        ) : (
          <tbody>
            {projects.map(project => (
              <tr
                key={project.id}
                className={classNames(styles.row, styles.selectableRow)}
                tabIndex={0}
                onClick={event => {
                  if (!isInteractiveElementClick(event)) {
                    openProject(project.id)
                  }
                }}
                onKeyDown={event => {
                  if (event.key === 'Enter') {
                    openProject(project.id)
                  }
                }}
                aria-label={`Open ${project.name}`}
              >
                <td className={styles.cell}>
                  <span className={styles.projectName}>{project.name}</span>
                  {project.description && <span className={styles.projectDescription}>{project.description}</span>}
                </td>
                <td className={styles.cell}>
                  <RepositoryNames
                    names={(project._embedded?.repositories._embedded.items ?? []).map(
                      repository => repository.full_name
                    )}
                    total={project.repository_count}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        )}
      </table>
    </div>
  )
}
